// Product image. Uses the index `image` when present, otherwise the uploaded placeholder
// (public/sample-docs/placeholder.svg). If that file is missing, falls back to an inline
// DOKA-branded mark so the build never shows a broken image.
//
// Every candidate URL runs through `useAssetUrl` first: images live in a private Blob Storage
// container, so the URL only becomes fetchable once the SAS token is there (see blobAssets.ts).
// Signing here rather than in the provider keeps cached/persisted URLs free of an expiring token -
// and when the token is renewed, the new src makes the browser reload the image by itself.

import { useEffect, useState } from 'react';

import { brand } from '../../branding';
import { useAssetUrl } from '../blobAssets';

const PLACEHOLDER_URL = `${import.meta.env.BASE_URL}sample-docs/placeholder.svg`;

const DokaMark = ({ size }: { size: number }) => (
    <svg height={size} role="img" viewBox="0 0 100 100" width={size} aria-hidden>
        <rect fill="none" height="84" rx="2" stroke={brand.primary} strokeWidth="5" width="84" x="8" y="8" />
        <polygon fill={brand.primary} points="50,30 84,76 16,76" />
        <polyline fill="none" points="20,69 50,44 80,69" stroke="#ffffff" strokeLinejoin="round" strokeWidth="5" />
        <polyline fill="none" points="30,76 50,60 70,76" stroke="#ffffff" strokeLinejoin="round" strokeWidth="5" />
    </svg>
);

interface ProductImageProps {
    src?: string | null;
    /**
     * Tried if `src` fails to load (e.g. `src` is a thumbnail URL under assets/tn/, which 404s
     * when the optional tn/ inbox stream hasn't been delivered yet, but the original image under
     * assets/ has). Only the placeholder mark shows if this ALSO fails or is not given.
     */
    fallbackSrc?: string | null;
    alt?: string;
    height?: number;
    /** Defaults to filling the parent's width (grid tiles, table cells, the 280px "enlarge"
     * buttons already constrain that externally). Pass an explicit value - typically equal to
     * `height` for a square thumbnail - for callers that sit in a flex row with no other width
     * constraint (e.g. a header thumbnail next to a title), otherwise the div stretches to
     * whatever space the flex row leaves it instead of staying small. */
    width?: number | string;
}

export const ProductImage = ({ src, fallbackSrc, alt = '', height = 120, width = '100%' }: ProductImageProps) => {
    const signUrl = useAssetUrl();
    // `undefined` = "needs a SAS token and none is there yet", `null` = "no URL at all" - see
    // signAssetUrl. Anything else is ready to load.
    const signedSrc = signUrl(src);
    const signedFallback = signUrl(fallbackSrc);
    const waitingForToken = signedSrc === undefined || signedFallback === undefined;

    // Starts true (skip straight to fallbackSrc) when src itself is absent from the start (e.g.
    // no thumbnail was ever resolved) - not just when a present src fails to *load*. Otherwise a
    // missing src would render the placeholder immediately without ever trying fallbackSrc.
    const [useFallback, setUseFallback] = useState(!src);
    const [broken, setBroken] = useState(false);

    // Re-evaluate both whenever the candidates change, so a previously broken/fallen-back image
    // does not keep showing stale state for a different article/tile - and so a refreshed SAS
    // token clears a `broken` that was only ever caused by the old one expiring.
    useEffect(() => {
        setUseFallback(!signedSrc);
        setBroken(false);
    }, [signedSrc, signedFallback]);

    const candidate = useFallback ? signedFallback : signedSrc;
    const effectiveSrc = candidate || PLACEHOLDER_URL;
    const canStillFallBack = !useFallback && !!signedFallback;

    return (
        // The rounded, bordered box is the point, not decoration (see design review,
        // point 3): the source images are cut-outs with wildly different aspect ratios - a 1.80m
        // beam is a long shallow diagonal, a stub is nearly square - so drawn straight onto white
        // they gave every list row a different height and a different optical centre. A fixed box
        // with `object-fit: contain` inside it turns that into one straight column. The border is
        // what keeps the box readable where the artwork itself is pale or mostly white.
        // Frame and fill inline, from the app's grey tokens: the Tailwind gray-200/gray-50 classes
        // came out as a dark frame under @uag/doka-design (reported: "nur einen leicht erkennbaren
        // grauen Rand, nicht dunkel" - table and tiles alike).
        <div
            className="flex shrink-0 items-center justify-center overflow-hidden"
            style={{
                height,
                width,
                border: '1px solid var(--pih-border)',
                borderRadius: 6,
                backgroundColor: '#fff'
            }}
        >
            {waitingForToken ? null : broken ? (
                <DokaMark size={Math.max(20, Math.min(48, height - 12))} />
            ) : (
                <img
                    alt={alt}
                    // Explicit width/height (not just the CSS max-* below) - Mantine's Table uses
                    // table-layout:auto, which sizes columns from cell CONTENT. Without these, the
                    // browser has no known image size before it loads; once each row's real (often
                    // much larger) source image resolves - at a different time per row, over the
                    // network - the column width gets recomputed and the whole table reflows,
                    // which is exactly the row-by-row "Flackern" reported in chat. A stable
                    // intrinsic size from the very first paint avoids that reflow entirely.
                    // Native lazy loading: a list page can hold 50-500 rows, and without this every
                    // single thumbnail is requested the moment the page renders - hundreds of
                    // parallel blob requests for the handful of rows actually on screen. The
                    // explicit width/height above is what makes this safe (the browser knows the
                    // box before the image arrives, so nothing reflows as they trickle in).
                    // `decoding="async"` keeps the decode off the main thread as they do.
                    decoding="async"
                    height={height}
                    loading="lazy"
                    src={effectiveSrc}
                    style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                    width={height}
                    onError={() => {
                        if (canStillFallBack) {
                            setUseFallback(true);
                        } else {
                            setBroken(true);
                        }
                    }}
                />
            )}
        </div>
    );
};
